import { type AskPunttChatUserSummary } from "@mg/schemas/src/christo/askPuntt";
import { X } from "@phosphor-icons/react";
import {
  Avatar,
  Button,
  Dialog,
  Flex,
  IconButton,
  Text,
} from "@radix-ui/themes";

import { AskPunttInvitePicker } from "./AskPunttInvitePicker";

import { useUI } from "../../../contexts/ui";
import {
  captureAskPunttInviteSent,
  type AskPunttInviteRecipientTier,
} from "../../../utils/askPunttInviteAnalytics";
import { useAnalytics } from "../../../utils/analytics";
import { useAppSelector } from "../../../utils/hooks";
import {
  useShareAskPunttChat,
  useUnshareAskPunttChat,
} from "../../../utils/queries/askPuntt";
import { type InviteTier } from "../utils/classifyInviteCandidate";

interface ShareAskPunttChatDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  chatId: string;
  chatTitle?: string;
  members: AskPunttChatUserSummary[];
  /** When the chat belongs to a project, pass roster for tier-1 tagging. */
  projectId?: string | null;
  projectName?: string;
  projectMembers?: AskPunttChatUserSummary[];
}

/**
 * Owner-facing dialog for sharing an Ask Puntt chat. Uses the shared tiered
 * invite picker and emits `ask_puntt_invite_sent` on each successful share.
 */
export function ShareAskPunttChatDialog({
  open,
  onOpenChange,
  chatId,
  chatTitle,
  members,
  projectId = null,
  projectName,
  projectMembers,
}: ShareAskPunttChatDialogProps) {
  const { notify } = useUI();
  const { capture } = useAnalytics("ShareAskPunttChatDialog");
  const authUser = useAppSelector((state) => state.auth.value);
  const shareMutation = useShareAskPunttChat(chatId);
  const unshareMutation = useUnshareAskPunttChat(chatId);

  const trackInvite = (
    response: Awaited<ReturnType<typeof shareMutation.mutateAsync>>,
    tiers: AskPunttInviteRecipientTier[],
  ) => {
    if (!authUser?.enterpriseId || !authUser.userID) return;
    captureAskPunttInviteSent(capture, {
      senderUserId: authUser.userID,
      senderEmail: authUser.email,
      recipientUserIds: response.inviteSummary?.recipientUserIds ?? [],
      recipientEmails: response.inviteSummary?.recipientEmails ?? [],
      recipientTiers: response.inviteSummary?.recipientTiers ?? tiers,
      enterpriseId: authUser.enterpriseId,
      enterpriseName: authUser.enterpriseName,
      projectId: projectId ?? null,
      projectName,
      chatId,
      surface: "chat_dialog",
    });
  };

  const handleInviteUser = async (
    user: AskPunttChatUserSummary,
    tier: InviteTier,
  ) => {
    if (tier === "new_email") return;
    try {
      const response = await shareMutation.mutateAsync({ userIds: [user._id] });
      trackInvite(response, [tier]);
      notify({
        title: "Chat shared",
        message: `Shared with ${user.name || user.email || "user"}.`,
      });
    } catch {
      // Mutation hook already shows an error toast.
    }
  };

  const handleInviteEmail = async (email: string) => {
    try {
      const response = await shareMutation.mutateAsync({ emails: [email] });
      trackInvite(response, ["new_email"]);
      notify({
        title: "Invitation sent",
        message: `They'll receive an email to join your organization and this chat.`,
      });
    } catch {
      // Error toast handled by mutation hook.
    }
  };

  const handleRemove = async (user: AskPunttChatUserSummary) => {
    try {
      await unshareMutation.mutateAsync(user._id);
      notify({
        title: "Removed",
        message: `Removed ${user.name || user.email || "user"} from this chat.`,
      });
    } catch {
      // Mutation hook already shows an error toast.
    }
  };

  const renderUserMeta = (user: AskPunttChatUserSummary) => {
    const display = user.name?.trim() || user.email?.trim() || user._id;
    const subtitle = user.name && user.email ? user.email : undefined;
    return (
      <Flex direction="column" className="min-w-0 flex-1">
        <Text size="2" weight="medium" className="truncate">
          {display}
        </Text>
        {subtitle ? (
          <Text size="1" color="gray" className="truncate">
            {subtitle}
          </Text>
        ) : null}
      </Flex>
    );
  };

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Content
        maxWidth="500px"
        data-testid="ask-puntt-share-chat-dialog"
        aria-labelledby="ask-puntt-share-chat-title"
      >
        <Dialog.Title id="ask-puntt-share-chat-title">Share chat</Dialog.Title>
        <Dialog.Description size="2" mb="3">
          {chatTitle
            ? `Give other people in your organization access to "${chatTitle}". They'll be able to read messages and reply.`
            : "Give other people in your organization access to this chat. They'll be able to read messages and reply."}
        </Dialog.Description>

        <AskPunttInvitePicker
          open={open}
          mode="chat"
          searchInputId="ask-puntt-share-search-input"
          searchTestId="ask-puntt-share-search-input"
          resultsTestId="ask-puntt-share-results"
          loadingTestId="ask-puntt-share-results-loading"
          emptyTestId="ask-puntt-share-results-empty"
          resultRowTestIdPrefix="ask-puntt-share-result-"
          addButtonTestIdPrefix="ask-puntt-share-add-"
          emailInviteTestId="ask-puntt-share-email-row"
          accessMembers={members}
          projectMembers={projectMembers}
          isPending={shareMutation.isPending}
          onInviteUser={handleInviteUser}
          onInviteEmail={handleInviteEmail}
        />

        <Flex direction="column" gap="1" mt="4">
          <Text size="2" weight="medium" id="ask-puntt-share-members-heading">
            People with access
          </Text>
          {members.length === 0 ? (
            <Text
              size="2"
              color="gray"
              data-testid="ask-puntt-share-members-empty"
            >
              Only you. Search above to share with teammates.
            </Text>
          ) : (
            <Flex
              direction="column"
              gap="1"
              role="list"
              aria-labelledby="ask-puntt-share-members-heading"
            >
              {members.map((user) => (
                <Flex
                  key={user._id}
                  align="center"
                  gap="2"
                  data-testid={`ask-puntt-share-member-${user._id}`}
                  role="listitem"
                  className="rounded-md px-2 py-1.5"
                >
                  <Avatar
                    src={user.avatar ?? undefined}
                    fallback={(user.name || user.email || "?")
                      .charAt(0)
                      .toUpperCase()}
                    size="1"
                    radius="full"
                  />
                  {renderUserMeta(user)}
                  <IconButton
                    data-testid={`ask-puntt-share-remove-${user._id}`}
                    aria-label={`Remove ${user.name || user.email || "user"} from shared list`}
                    variant="soft"
                    color="gray"
                    size="1"
                    disabled={unshareMutation.isPending}
                    onClick={() => handleRemove(user)}
                  >
                    <X size={14} aria-hidden="true" />
                  </IconButton>
                </Flex>
              ))}
            </Flex>
          )}
        </Flex>

        <Flex mt="4" justify="end">
          <Button
            variant="soft"
            color="gray"
            data-testid="ask-puntt-share-cancel-button"
            onClick={() => onOpenChange(false)}
          >
            Done
          </Button>
        </Flex>
      </Dialog.Content>
    </Dialog.Root>
  );
}
